import { useState, useEffect } from 'react'
import { BiCreditCard, BiDownload, BiRefresh } from 'react-icons/bi'
import paymentService, { Invoice, Subscription } from '../services/paymentService'
import { useCurrentUser } from '../hooks/useCurrentUser'

const Billing = () => {
  const { user } = useCurrentUser()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('')

  useEffect(() => {
    fetchBillingData()
  }, [])

  const fetchBillingData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Simulate API calls
      await Promise.all([
        new Promise(resolve => setTimeout(resolve, 1000)),
        new Promise(resolve => setTimeout(resolve, 1200))
      ])

      // Mock data
      const mockInvoices: Invoice[] = [
        {
          id: 'inv_1',
          number: 'INV-001',
          date: new Date('2024-01-15').toISOString(),
          amount: '4999',
          status: 'paid',
          description: 'Agency Base Plan - January 2024',
          subtotal: 4999,
          tax: 0,
          total: 4999,
          currency: 'gbp',
          periodStart: new Date('2024-01-01').toISOString(),
          periodEnd: new Date('2024-01-31').toISOString(),
          amount_paid: 4999,
          amount_due: 0,
          payment_intent: null,
          requires_action: false
        },
        {
          id: 'inv_2',
          number: 'INV-002',
          date: new Date('2024-01-10').toISOString(),
          amount: '1499',
          status: 'paid',
          description: 'Additional Seat - January 2024',
          subtotal: 1499,
          tax: 0,
          total: 1499,
          currency: 'gbp',
          periodStart: new Date('2024-01-01').toISOString(),
          periodEnd: new Date('2024-01-31').toISOString(),
          amount_paid: 1499,
          amount_due: 0,
          payment_intent: null,
          requires_action: false
        }
      ]

      const mockSubscriptions: Subscription[] = [
        {
          id: 'sub_1',
          status: 'active',
          current_period_start: new Date('2024-01-01').toISOString(),
          current_period_end: new Date('2024-02-01').toISOString(),
          customer: {
            id: 'cus_1',
            email: 'customer@example.com',
            name: 'Agency Base Plan'
          },
          items: [{
            id: 'si_1',
            price: {
              id: 'price_1',
              currency: 'gbp',
              unit_amount: 4999,
              recurring: {
                interval: 'month',
                interval_count: 1
              }
            },
            quantity: 1
          }],
          latest_invoice: {
            id: 'inv_latest_1',
            status: 'paid',
            total: 4999,
            currency: 'gbp'
          },
          discount: null,
          cancel_at_period_end: false,
          canceled_at: null,
          created: new Date('2024-01-01').toISOString(),
          metadata: {}
        }
      ]

      setInvoices(mockInvoices)
      setSubscriptions(mockSubscriptions)
    } catch (err: any) {
      console.error('Error fetching billing data:', err)
      setError('Failed to load billing information. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      setMessage('')
      // Simulate download
      await new Promise(resolve => setTimeout(resolve, 1000))
      setMessage('Invoice downloaded successfully!')
      setMessageType('success')
    } catch (err: any) {
      console.error('Error downloading invoice:', err)
      setMessage('Failed to download invoice. Please try again.')
      setMessageType('error')
    }
  }

  const formatCurrency = (amount: number, currency: string = 'gbp') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'active':
        return 'text-green-600 bg-green-100 dark:bg-green-900/20'
      case 'pending':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20'
      case 'failed':
      case 'canceled':
        return 'text-red-600 bg-red-100 dark:bg-red-900/20'
      default:
        return 'text-slate-600 bg-slate-100 dark:bg-slate-900/20'
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Billing & Invoices</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-2">
          Manage your billing information and view invoices.
        </p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${messageType === 'success'
          ? 'bg-green-50 border border-green-200 text-green-800'
          : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
          <div className="flex justify-between items-center">
            <span>{message}</span>
            <button
              onClick={() => setMessage('')}
              className="text-slate-400 hover:text-slate-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Subscriptions */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Active Subscriptions</h2>
            <button
              onClick={fetchBillingData}
              className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <BiRefresh className="w-5 h-5" />
            </button>
          </div>

          {subscriptions.length === 0 ? (
            <p className="text-slate-500 dark:text-slate-400">No active subscriptions found.</p>
          ) : (
            <div className="space-y-4">
              {subscriptions.map((subscription) => (
                <div
                  key={subscription.id}
                  className="border border-slate-200 dark:border-slate-700 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-slate-900 dark:text-white">
                      {subscription.customer.name || 'Subscription'}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(subscription.status)}`}>
                      {subscription.status}
                    </span>
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    <p>Amount: {formatCurrency(subscription.items[0].price.unit_amount, subscription.items[0].price.currency)}</p>
                    <p>Period: {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* . */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Recent Invoices</h2>
            <button
              onClick={fetchBillingData}
              className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <BiRefresh className="w-5 h-5" />
            </button>
          </div>

          {invoices.length === 0 ? (
            <p className="text-slate-500 dark:text-slate-400">No invoices found.</p>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="border border-slate-200 dark:border-slate-700 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-slate-900 dark:text-white">
                      {invoice.description}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                    <p>Amount: {formatCurrency(parseInt(invoice.amount), invoice.currency)}</p>
                    <p>Date: {formatDate(invoice.date)}</p>
                  </div>
                  <button
                    onClick={() => handleDownloadInvoice(invoice.id)}
                    className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                  >
                    <BiDownload className="mr-1" />
                    Download PDF
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Billing 